import React, { useCallback, useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material";
import {
  Campaign as AnnouncementIcon,
  Subject as SubjectIcon,
  People as PeopleIcon,
  AttachFile as AttachFileIcon,
  Search as SearchIcon,
  AutoFixHigh as AutoFixHighIcon,
  Category as CategoryIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { DataGrid } from "@mui/x-data-grid";
import "../../styles/UserStyles.css";
import "../../styles/App.scss";
import { ANNOUNCEMENTS } from "../../component/common/Routes/AppRoutes";
import ControlledTextInputField from "../../component/ui/FormFields/ControlledTextInputField";
import CustomButton from "../../component/ui/FormFields/Button";
import {
  createAnnouncementRequest,
  resetAnnouncementState,
} from "../../redux/Announcements/announcementActions";
import { generateAnnouncementAI } from "../../redux/Announcements/announcementService";
import { fetchAllCustomersRequest } from "../../redux/Customers/actions/customerActions";
import { selectCountryRequest } from "../../redux/Common/selectorAction";

export default function CreateAnnouncement() {
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      theme: "",
      content: "",
      recipientType: "all",
      customerIds: [],
    },
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Filter states
  const [filterStatus, setFilterStatus] = useState("");
  const [filterBookingCount, setFilterBookingCount] = useState("");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [filterCountry, setFilterCountry] = useState("");

  const { loading, error, successMessage } = useSelector(
    (state) => state.announcements,
  );
  const user = useSelector((state) => state.auth?.user || {});
  const [isGenerating, setIsGenerating] = useState(false);
  const customers = useSelector(
    (state) => state.customers?.customers?.data || [],
  );
  const totalRecords = useSelector(
    (state) => state.customers?.customers?.totalRecords || 0,
  );
  const customersLoading = useSelector(
    (state) => state.customers?.customers?.loading,
  );
  const countryList = useSelector(
    (state) => state.selector?.country?.data || [],
  );

  const recipientType = watch("recipientType");
  const selectedCustomerIds = watch("customerIds");
  const themeValue = watch("theme");
  const contentValue = watch("content");

  useEffect(() => {
    dispatch(fetchAllCustomersRequest());
    dispatch(selectCountryRequest());
  }, [dispatch]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(resetAnnouncementState());
      navigate(ANNOUNCEMENTS);
    }
    if (error) {
      toast.error(error);
      dispatch(resetAnnouncementState());
    }
  }, [successMessage, error, navigate, dispatch]);

  // Local search filter only (API handles the other 4 filters server-side)
  const filteredCustomers = customers.filter((customer) => {
    const fullName =
      `${customer.first_name} ${customer.last_name}`.toLowerCase();
    return (
      fullName.includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm)
    );
  });

  // API call helper
  const fetchFilteredCustomers = useCallback(
    (status, bookingCount, fromDate, toDate, country) => {
      const params = {};
      if (status !== "") params.status = status;
      if (bookingCount !== "") params.booking_count = bookingCount;
      if (fromDate !== "") params.fromDate = fromDate;
      if (toDate !== "") params.toDate = toDate;
      if (country !== "") params.country_id = country;
      dispatch(
        fetchAllCustomersRequest(
          Object.keys(params).length ? params : undefined,
        ),
      );
    },
    [dispatch],
  );

  // Debounced API call whenever any filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchFilteredCustomers(
        filterStatus,
        filterBookingCount,
        filterFromDate,
        filterToDate,
        filterCountry,
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [
    filterStatus,
    filterBookingCount,
    filterFromDate,
    filterToDate,
    filterCountry,
    fetchFilteredCustomers,
  ]);

  const handleClearFilters = () => {
    setFilterStatus("");
    setFilterBookingCount("");
    setFilterFromDate("");
    setFilterToDate("");
    setFilterCountry("");
    setSearchTerm("");
    dispatch(fetchAllCustomersRequest());
  };

  const columns = [
    {
      field: "full_name",
      headerName: "Customer",
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {params.row.first_name} {params.row.last_name}
          </Typography>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 150,
    },
    {
      field: "booking_count",
      headerName: "Bookings",
      width: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.4,
              borderRadius: 1,

              fontSize: "0.78rem",
              fontWeight: 700,
            }}
          >
            {params.row.booking_count ?? 0}
          </Box>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const isActive = params.row.status === 1 || params.row.status === "1";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: isActive ? "#e8f5e9" : "#fce4ec",
                color: isActive ? "#2e7d32" : "#c62828",
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              {isActive ? "Active" : "Inactive"}
            </Box>
          </Box>
        );
      },
    },
  ];

  const handleGenerateContent = async () => {
    if (!themeValue?.trim()) {
      toast.error("Please enter a theme first.");
      return;
    }

    setIsGenerating(true);
    try {
      const response = await generateAnnouncementAI({
        theme: themeValue?.trim() || "",
        subject: "",
        role: user?.role || "Admin",
        org_name: user?.org_name || "Atithiseva",
        tone: "neutral",
        length: "medium",
      });

      if (response.content) {
        setValue("content", response.content);
      }

      if ((!themeValue?.trim() || themeValue === "General") && response.theme) {
        setValue("theme", response.theme);
      }
    } catch (error) {
      console.error("AI Generation Failed:", error);
      toast.error(error.message || "Failed to generate content.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBlur = () => {
    if (themeValue?.trim() && !contentValue?.trim()) {
      handleGenerateContent();
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Strip the "data:image/...;base64," prefix
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const onSubmit = async (data) => {
    if (
      data.recipientType === "specific" &&
      (!data.customerIds || data.customerIds.length === 0)
    ) {
      toast.error("Please select at least one customer");
      return;
    }

    try {
      let base64Image = "";
      if (data.attachment && data.attachment[0]) {
        base64Image = await fileToBase64(data.attachment[0]);
      }

      const payload = {
        name: data.theme,
        description: data.content,
        type: data.recipientType === "all" ? "all" : "particular",
        image: base64Image,
        date: new Date().toISOString().split("T")[0],
      };

      if (data.recipientType === "specific") {
        payload.customer_id = data.customerIds;
      }

      dispatch(createAnnouncementRequest(payload));
    } catch (error) {
      console.error("Payload preparation failed:", error);
      toast.error("Failed to process attachment image.");
    }
  };

  const formFields = [
    {
      name: "theme",
      label: "Theme",
      placeholder: "Enter Theme",
      icon: <CategoryIcon />,
      sm: 12, // Take full width now that subject is gone
      xs: 12,
    },
    {
      name: "content",
      label: "Content *",
      placeholder: "Enter Announcement Content",
      type: "textarea",
      rows: 6,
      rules: { required: "Content is required" },
      sm: 12,
      xs: 12,
    },
    {
      name: "attachment",
      label: "Attachment",
      type: "file",
      accept: "image/*",
      icon: <AttachFileIcon />,
      sm: 12,
      xs: 12,
    },
    {
      name: "recipientType",
      label: "Recipients *",
      type: "radio",
      radioOptions: [
        { value: "all", label: "All Customers" },
        { value: "specific", label: "Specific Customers" },
      ],
      rules: { required: "Recipient selection is required" },
      sm: 12,
      xs: 12,
    },
  ];

  return (
    <>
      <ToastContainer />
      <Box className="container">
        <Card
          sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
        >
          <CardContent className="card" sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
              <AnnouncementIcon
                sx={{ color: "#ff9800", fontSize: 32, mr: 2 }}
              />
              <Typography
                variant="h5"
                className="form-heading"
                sx={{ mb: 0, fontWeight: 700 }}
              >
                Create New Announcement
              </Typography>
            </Box>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid
                container
                rowSpacing={4}
                columnSpacing={{ xs: 1, sm: 1, md: 1, lg: 3 }}
              >
                {formFields.slice(0, 3).map((field) => (
                  <Grid item xs={field.xs} sm={field.sm} key={field.name}>
                    <ControlledTextInputField
                      name={field.name}
                      control={control}
                      label={field.label}
                      placeholder={field.placeholder}
                      startAdornment={field.icon}
                      endAdornment={
                        field.name === "theme" ? (
                          <IconButton
                            onClick={handleGenerateContent}
                            disabled={isGenerating}
                            edge="end"
                            sx={{ color: "#ff9800" }}
                          >
                            {isGenerating ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <AutoFixHighIcon />
                            )}
                          </IconButton>
                        ) : null
                      }
                      rules={field.rules}
                      type={field.type}
                      accept={field.accept}
                      rows={field.rows}
                      onBlur={handleBlur}
                      error={!!errors[field.name]}
                      helperText={errors[field.name]?.message}
                    />
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <PeopleIcon sx={{ color: "#ff9800" }} fontSize="small" />{" "}
                    Recipients
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box
                        onClick={() => setValue("recipientType", "all")}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: "2px solid",
                          borderColor:
                            recipientType === "all" ? "#ff9800" : "#eee",
                          bgcolor:
                            recipientType === "all"
                              ? "rgba(255, 152, 0, 0.04)"
                              : "white",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          transition: "0.2s",
                          "&:hover": { borderColor: "#ff9800" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: "2px solid",
                            borderColor:
                              recipientType === "all" ? "#ff9800" : "#ccc",
                            mr: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {recipientType === "all" && (
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#ff9800",
                              }}
                            />
                          )}
                        </Box>
                        <Typography fontWeight={600} sx={{ flexGrow: 1 }}>
                          Send to All Customers
                        </Typography>
                        <Box
                          sx={{
                            bgcolor: "#4caf50",
                            color: "white",
                            px: 1,
                            py: 0.2,
                            borderRadius: 5,
                            fontSize: "0.7rem",
                          }}
                        >
                          {totalRecords} members
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        onClick={() => setValue("recipientType", "specific")}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: "2px solid",
                          borderColor:
                            recipientType === "specific" ? "#ff9800" : "#eee",
                          bgcolor:
                            recipientType === "specific"
                              ? "rgba(255, 152, 0, 0.04)"
                              : "white",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          transition: "0.2s",
                          "&:hover": { borderColor: "#ff9800" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: "2px solid",
                            borderColor:
                              recipientType === "specific" ? "#ff9800" : "#ccc",
                            mr: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {recipientType === "specific" && (
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#ff9800",
                              }}
                            />
                          )}
                        </Box>
                        <Typography fontWeight={600}>
                          Send to Particular Customer
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                {recipientType === "specific" && (
                  <Grid item xs={12}>
                    {/* Filter Panel */}
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        border: "1px solid #eee",
                        borderRadius: 2,
                        bgcolor: "#fafafa",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                          gap: 1,
                          justifyContent: "space-between",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <FilterListIcon
                            sx={{ color: "#ff9800", fontSize: 20 }}
                          />
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            color="#555"
                          >
                            Filter Customers
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleClearFilters}
                          sx={{
                            borderColor: "#ff9800",
                            color: "#ff9800",
                            textTransform: "none",
                            fontSize: "0.75rem",
                            py: 0.3,
                            "&:hover": {
                              borderColor: "#f57c00",
                              color: "#f57c00",
                              bgcolor: "rgba(255,152,0,0.05)",
                            },
                          }}
                        >
                          Clear Filters
                        </Button>
                      </Box>
                      <Grid container spacing={2}>
                        {/* Status Filter */}
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={filterStatus}
                              label="Status"
                              onChange={(e) => setFilterStatus(e.target.value)}
                            >
                              <MenuItem value="">All</MenuItem>
                              <MenuItem value="1">Active</MenuItem>
                              <MenuItem value="0">Inactive</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Booking Count Filter */}
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Min. Booking Count"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={filterBookingCount}
                            onChange={(e) =>
                              setFilterBookingCount(e.target.value)
                            }
                          />
                        </Grid>

                        {/* From Date */}
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="From Date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={filterFromDate}
                            onChange={(e) => setFilterFromDate(e.target.value)}
                          />
                        </Grid>

                        {/* To Date */}
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="To Date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={filterToDate}
                            onChange={(e) => setFilterToDate(e.target.value)}
                          />
                        </Grid>

                        {/* Country Filter */}
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Country</InputLabel>
                            <Select
                              value={filterCountry}
                              label="Country"
                              onChange={(e) => setFilterCountry(e.target.value)}
                            >
                              <MenuItem value="">All</MenuItem>
                              {countryList.map((c) => (
                                <MenuItem key={c.id} value={String(c.id)}>
                                  {c.country_name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box
                      sx={{
                        mt: 2,
                        border: "1px solid #eee",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <TextField
                        fullWidth
                        placeholder="Search by name, email, or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon color="action" />
                            </InputAdornment>
                          ),
                          sx: { py: 1.5, px: 2, fontSize: "0.9rem" },
                        }}
                        variant="standard"
                        sx={{
                          bgcolor: "white",
                          "& .MuiInput-underline:before, & .MuiInput-underline:after":
                            { display: "none" },
                        }}
                      />
                      <Box sx={{ height: 450, width: "100%" }}>
                        <DataGrid
                          rows={filteredCustomers}
                          columns={columns}
                          checkboxSelection
                          keepNonExistentRowsSelected={true}
                          onRowSelectionModelChange={(newSelectionModel) => {
                            setValue("customerIds", newSelectionModel);
                          }}
                          rowSelectionModel={selectedCustomerIds}
                          loading={customersLoading}
                          paginationModel={paginationModel}
                          onPaginationModelChange={setPaginationModel}
                          pageSizeOptions={[5, 10, 25]}
                          getRowId={(row) => row.id}
                          sx={{
                            border: "none",
                            "& .MuiDataGrid-columnHeader": {
                              backgroundColor: "#f5f5f5 !important",
                              color: "#333 !important",
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                              fontWeight: 700,
                              fontSize: "0.85rem",
                              color: "#333",
                            },
                            "& .MuiDataGrid-iconButtonContainer": {
                              color: "#333",
                            },
                            "& .MuiDataGrid-menuIcon": {
                              color: "#333",
                            },
                            "& .MuiDataGrid-row:nth-of-type(even)": {
                              backgroundColor: "#fafafa",
                            },
                            "& .MuiDataGrid-cell": {
                              borderBottom: "1px solid #f0f0f0",
                              fontSize: "0.85rem",
                            },
                            "& .MuiCheckbox-root": {
                              color: "#ff9800",
                            },
                            "& .MuiDataGrid-footerContainer": {
                              borderTop: "1px solid #eee",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      mt: 4,
                    }}
                  >
                    <CustomButton
                      variant="contained"
                      className="cancel-button"
                      type="button"
                      text={"Cancel"}
                      onClick={() => navigate(ANNOUNCEMENTS)}
                      sx={{
                        bgcolor: "#757575",
                        "&:hover": { bgcolor: "#616161" },
                      }}
                    />
                    <CustomButton
                      variant="contained"
                      className="success-button"
                      type="submit"
                      loading={loading}
                      text={"Send Announcement"}
                      sx={{
                        px: 4,
                        bgcolor: "#ff9800",
                        "&:hover": { bgcolor: "#f57c00" },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
